const AUTO_VISITOR_NAME =
  /^(Messenger|WhatsApp|Instagram|Telegram|Web|Chat web|Demo|Testchat|Facebook|SMS|Email|Slack|Teams)\s*[….·]/i;

/** Caracteres que parecen “vacío” en UI (Braille blank, fillers Hangul, zero-width, etc.). */
const INVISIBLE_OR_FILLER =
  /[\u00A0\u1680\u2000-\u200F\u2028\u2029\u202A-\u202F\u205F\u2060\u2066-\u2069\u2800\u3000\u3164\uFEFF\uFFA0\u115F\u1160\u180E\u200B-\u200D\uFE00-\uFE0F]/g;

export function sanitizeVisitorName(name: string | null | undefined): string {
  if (!name) return '';
  return name.replace(INVISIBLE_OR_FILLER, '').replace(/\s+/g, ' ').trim();
}

export function isAutoGeneratedVisitorName(name: string | null | undefined): boolean {
  const n = sanitizeVisitorName(name);
  if (!n) return false;
  return AUTO_VISITOR_NAME.test(n);
}

/** Formatea wa_id / thread externo a +XX … para mostrar cuando no hay nombre real. */
export function formatExternalThreadLabel(
  externalThreadId: string | null | undefined,
  channel?: string | null
): string | null {
  const raw = externalThreadId?.trim();
  if (!raw) return null;
  const digits = raw.replace(/\D/g, '');
  if (
    digits.length >= 8 &&
    (channel === 'whatsapp_meta' || channel === 'whatsapp_js' || /^whatsapp/i.test(channel ?? ''))
  ) {
    return `+${digits}`;
  }
  if (digits.length >= 8 && /^\d+$/.test(raw)) return `+${digits}`;
  return raw;
}

type DisplayVisitorOptions = {
  fallback?: string;
  externalThreadId?: string | null;
  channel?: string | null;
};

/** Nombre legible para UI; oculta etiquetas auto y nombres solo con caracteres invisibles. */
export function displayVisitorLabel(
  name: string | null | undefined,
  fallbackOrOpts: string | DisplayVisitorOptions = 'Visitante'
): string {
  const opts: DisplayVisitorOptions =
    typeof fallbackOrOpts === 'string' ? { fallback: fallbackOrOpts } : fallbackOrOpts;
  const fallback = opts.fallback ?? 'Visitante';
  const n = sanitizeVisitorName(name);
  if (n && !isAutoGeneratedVisitorName(n)) return n;
  const phone = formatExternalThreadLabel(opts.externalThreadId, opts.channel);
  if (phone) return phone;
  return fallback;
}

export function formatConversationDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatControlMode(mode: string): string {
  if (mode === 'human') return 'Humano';
  if (mode === 'auto') return 'Automático (IA)';
  return mode;
}

export function formatConversationStatus(status: string): string {
  if (status === 'open') return 'Abierta';
  if (status === 'closed') return 'Cerrada';
  return status;
}

/** Hilo en modo humano donde el último mensaje es del visitante (pendiente de ver / responder). */
export function isPendingHumanAttentionConversation(row: {
  status?: string | null;
  controlMode?: string | null;
  listMeta?: { lastMessagePreview?: { senderLabel?: string | null } | null } | null;
}): boolean {
  if (row.controlMode !== 'human') return false;
  if (row.status && row.status !== 'open') return false;
  return row.listMeta?.lastMessagePreview?.senderLabel === 'Visitante';
}
